import React from 'react';

// DS
import { COLOR_VARIANTS, Typography, TYPOGRAPHY_VARIANT } from '@tajawal/ct-web-design-system';

// styles
import Styled from './BaggageTooltip.styled';

// utils
import { buildTestId } from '@/utils/helpers.utils';
import { useTranslation } from 'react-i18next';
import {
  getAddedBaggageContent,
  getBaggageOfferText,
  getCabinBaggageContent,
  getCheckedBaggageContent,
  getUnderSeatBaggageContent,
} from '@/utils/content.utils';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';

// components
import { BaggageInfoItem } from '@/app-desktop/components/BaggageInfoItem';

// types
import { BaggageInfoModel } from '@tajawal/web-flights-sdk/lib/types/baggage.types';
import { BaggagePerTraveller } from '@/types/common.types';
import { Divider } from '@mui/material';

type BaggageTooltipProps = {
  cabin: BaggageInfoModel;
  checked: BaggageInfoModel;
  underSeatBaggage: BaggageInfoModel;
  testIdPrefix: string;
  baggageOfferText?: string;
  hasFareFamilies: boolean;
  addedBaggageInfo?: BaggagePerTraveller[];
};

function BaggageTooltip({
  cabin,
  checked,
  underSeatBaggage,
  testIdPrefix,
  baggageOfferText = '',
  hasFareFamilies,
  addedBaggageInfo,
}: BaggageTooltipProps): JSX.Element {
  const titleTestId = buildTestId(testIdPrefix, 'Title');
  const { t } = useTranslation();

  const isCabinIncluded = _get(cabin, 'isIncluded', false);
  const isCheckedIncluded = _get(checked, 'isIncluded', false);
  const isUnderSeatIncluded = _get(underSeatBaggage, 'isIncluded', false);

  const cabinBaggageContent = getCabinBaggageContent({
    baggage: cabin,
    t,
  });
  const checkInBaggageContent = getCheckedBaggageContent({
    baggage: checked,
    t,
  });
  const underSeatBaggageContent = getUnderSeatBaggageContent({
    underSeatBaggage,
    t,
  });

  const baggageOffer = getBaggageOfferText({ t, isCheckedIncluded, hasFareFamilies });

  const hasAddedBaggage = !_isEmpty(addedBaggageInfo);

  return (
    <Styled.BaggageTooltipWrapper>
      <Typography
        component="h4"
        color={COLOR_VARIANTS.WHITE}
        variant={TYPOGRAPHY_VARIANT.HEADING4SEMBLD}
        testId={titleTestId}
      >
        {t('FareFamilyCard.Baggage')}
      </Typography>

      <Styled.BaggageInfoBoxWrapper>
        {isUnderSeatIncluded && (
          <BaggageInfoItem
            isIncluded={isUnderSeatIncluded}
            baggageText={underSeatBaggageContent}
            testIdPrefix={testIdPrefix}
            isUnderSeatBaggage
          />
        )}
        <BaggageInfoItem isIncluded={isCabinIncluded} baggageText={cabinBaggageContent} testIdPrefix={testIdPrefix} />
        <BaggageInfoItem
          isChecked
          baggageOptionsAvailable={!!baggageOffer}
          isIncluded={!!baggageOffer || isCheckedIncluded}
          baggageText={baggageOffer || checkInBaggageContent}
          testIdPrefix={testIdPrefix}
        />

        {baggageOfferText && (
          <BaggageInfoItem
            baggageText={{
              primary: baggageOfferText,
              secondary: '',
            }}
            testIdPrefix={testIdPrefix}
            baggageOptionsAvailable={!!baggageOfferText}
          />
        )}
      </Styled.BaggageInfoBoxWrapper>
      {hasAddedBaggage && (
        <>
          <Divider />
          <Typography variant={TYPOGRAPHY_VARIANT.HEADING4SEMBLD} component="h4" color={COLOR_VARIANTS.WHITE}>
            {t('Pax.FlightSummary_ExtraBaggageTitle')}
          </Typography>
          {addedBaggageInfo.map((addedBaggage) => {
            const baggageText = getAddedBaggageContent(addedBaggage, t);

            return (
              <BaggageInfoItem
                isExtra
                isIncluded
                baggageOptionsAvailable={hasAddedBaggage}
                testIdPrefix={testIdPrefix}
                // @ts-ignore we can pass a JSX element
                baggageText={baggageText}
              />
            );
          })}
        </>
      )}
    </Styled.BaggageTooltipWrapper>
  );
}

export default BaggageTooltip;
